import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoanAiService } from '../loanAiService';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DUMMY_PAYLOADS } from '../constant';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { finalize, Subscription } from 'rxjs';
import { Subject, interval } from 'rxjs';
import {
  switchMap,
  takeWhile,
  takeUntil,
  tap,
  distinctUntilChanged,
} from 'rxjs/operators';
import { RefreshDialogComponent } from '../_dialog/refresh-dialog/refresh-dialog.component';
import { ProfileService } from '../_services/profile.service';
import { LayoutService } from '../_services/layout.service';
import { MotifNavigationIcRefresh24px } from '@ey-xd/motif-icon';
import {
  MotifButtonModule,
  MotifIconModule,
  MotifToastModule,
  MotifProgressLoaderModule,
  MotifPaginationModule,
} from '@ey-xd/ng-motif';

interface Option {
  disable: boolean;
  value: any;
  name: string;
  id: string | number;
}
import { IngestionStatusPopupComponent } from '../_dialog/ingestion-status-popup/ingestion-status-popup.component';
import { environment } from '../../environments/environment';
import { CookieConsentService } from '../_services/cookie-consent.service';

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Info = 'info',
  Warning = 'warning',
}
@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MotifProgressLoaderModule,
    MatTableModule,
    MatTabsModule,
    MatExpansionModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    MotifProgressLoaderModule,
    MotifButtonModule,
    MotifIconModule,
    MotifToastModule,
    MotifPaginationModule,
  ],
  standalone: true,
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit {
  private readonly PAGE_SIZE_STORAGE_KEY = 'landing_page_size';
  // Track active polling for (year, quarter) to prevent duplicate polling
  private tenantIdSubscription: Subscription | null = null;
  selectedEngagementIndex: number = -1;
  private activePolls = new Set<string>();
  readonly dialog = inject(MatDialog);
  private destroy$ = new Subject<void>();
  readonly panelOpenState = signal(false);
  toastType: ToastType = ToastType.Info;
  showToast: boolean = false;
  type: any;
  ToastType: any;
  toastMessage: string = '';
  refreshData: any[] = [];
  statusData: any;
  requestId = '';
  showLoader = false;
  processingKey: string | null = null;
  client_name: any;
  engName: any;
  percentageCompletion = '--';
  ingestion_timestamp: any;
  username = '';
  expandPanel = false;
  tooltipRowIndex: number | null = null;
  pageSizeOptions: Option[] = [
    { name: '5', value: 5, id: 5, disable: false },
    { name: '10', value: 10, id: 10, disable: false },
    { name: '20', value: 20, id: 20, disable: false },
    { name: '50', value: 50, id: 50, disable: false },
    { name: '100', value: 100, id: 100, disable: false },
  ];
  selectedPageSize: Option = this.pageSizeOptions[0];
  pageSize = this.pageSizeOptions[0].value;
  currentPage = 1;
  pagedData: any[] = [];
  numberOfPages = 1;
  refreshIcon = MotifNavigationIcRefresh24px;
  showCookieAlert: boolean = false;

  constructor(
    private router: Router,
    private service: LoanAiService,
    private profileService: ProfileService,
    private layoutService: LayoutService,
    private cookieConsent: CookieConsentService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.layoutService.displaySideNav(true);
    this.checkCookieConsent();
    this.profileService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.username = this.profileService.getUsername();
      }
    });
    this.username = this.profileService.getUsername();
    // load summary data now; details render when accordion expands
    // this.runApi();
    this.client_name = DUMMY_PAYLOADS[0].engagement_name;
    //.pipe(distinctUntilChanged())
    this.tenantIdSubscription = this.service.tenantId$.subscribe((tenantId) => {
      if (tenantId) {
        // console.log(`In Landing page ${tenantId}`);
        this.runApi();
      }
    });
    // Load persisted page size (if any) so selection survives navigation
    try {
      const saved = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        const opt = this.pageSizeOptions.find((o) => o.value === parsed);
        if (opt) {
          this.selectedPageSize = opt;
          this.pageSize = opt.value;
        }
      }
    } catch (e) {
      // ignore
    }
    this.updatePagination();
  }

  private computeInitials(name: string | null | undefined): string {
    if (!name) return '';
    const trimmed = String(name).trim();
    if (!trimmed) return '';
    // Split on spaces and common separators first
    const coarseParts = trimmed.split(/[\s_\-./]+/).filter(Boolean);
    // Further split each part on camelCase or PascalCase boundaries
    const fineParts: string[] = [];
    const wordRegex = /[A-Z]+(?=[A-Z][a-z]|\d|$)|[A-Z]?[a-z]+|\d+/g;
    for (const part of coarseParts) {
      const matches = part.match(wordRegex);
      if (matches) {
        fineParts.push(...matches);
      } else {
        fineParts.push(part);
      }
    }

    const initials = fineParts.map(w => w.charAt(0).toUpperCase()).join('');
    // Limit to 2-3 chars to keep avatar readable
    return initials.slice(0, 3);
  }

  get clientInitials(): string {
    return this.computeInitials(this.client_name);
  }

  /** Return avatar style (background color) based on client name */
  get clientAvatarStyle(): { [key: string]: string } {
    const name = String(this.client_name ?? '').trim();
    const palette: Record<string, string> = {
      // Specific banks
      'TEST BANK': '#1976d2',    // teal
      'NEXBANK': '#7B6CFF',      // purple
      'APPLE BANK': '#5E81FF',   // indigo
      'LOAN FILE REVIEW': '#ff61d7ff', // coral
    };

    const key = name.toUpperCase();
    const bg = palette[key] ?? this.hashColor(name);
    return { background: bg };
  }

  /** Deterministic fallback color from name */
  private hashColor(str: string): string {
    if (!str) return '#7b6cff';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    // Map hash to HSL for pleasant colors
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 55%)`;
  }

  get isAnyInProgress(): boolean {
    return (
      this.refreshData?.some((d: any) => d.overall_status === 'IN_PROGRESS') ??
      false
    );
  }

  getDisplayStatus(status: string): string {
    if (!status) return '';
    const map: Record<string, string> = {
      FAILED: 'Failed',
      PARTIAL_COMPLETED: 'Partial Completed',
      COMPLETED: 'Completed',
      PENDING: 'Pending',
      IN_PROGRESS: 'In Progress',
    };
    return (
      map[status.toUpperCase()] ||
      status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    );
  }

  isProcessDisabled(status: string): boolean {
    if (!status) return false;
    const enabledStatuses = [
      'FAILED',
      'PARTIAL_COMPLETED',
      'COMPLETED',
      'PENDING',
    ];
    return !enabledStatuses.includes((status || '').toUpperCase());
  }

  /** Trigger the SharePoint fetch and block UI with full-screen loader */
  onFetchData() {
    this.showLoader = true;
    this.service
      .FetchSharepoint()
      .pipe(finalize(() => (this.showLoader = false)))
      .subscribe({
        next: (data) => {
          if (data.status === 'success') {
            const totalFiles = data.stats.reduce(
              (sum: number, d: any) => sum + d.files_recorded_in_db,
              0
            );
            this.openRefreshDialog(totalFiles);
          } else {
            this.toastError('while fetching data from SharePoint');
          }
        },
        error: () => {
          this.toastError('while fetching data from SharePoint');
        },
      });
  }

  /** Show an error toast for the given context */
  private toastError(ctx: string): void {
    this.toastType = ToastType.Error;
    this.showToast = true;
    this.toastMessage = `There was an error ${ctx}`;
    setTimeout(() => (this.showToast = false), 3000);
  }

  runApi() {
    this.showLoader = true;
    this.service.runApi().subscribe({
      next: (data: any[]) => {
        this.showLoader = false;
        this.panelOpenState.set(true);
        this.expandPanel = true;
        this.refreshData = data.map((d: any) => ({
          ...d,
          enable_ingestion: typeof d.enable_ingestion === 'boolean' ? d.enable_ingestion : true,
        }));
        this.selectedEngagementIndex = 0; // Reset to first row on data load
        this.updatePagination();
        this.refreshData
          .filter(
            (d: any) => d.overall_status === 'IN_PROGRESS' && d.request_id
          )
          .forEach((d: any) => {
            const pollKey = `${d.year}_${d.quarter}`;
            if (!this.activePolls.has(pollKey)) {
              this.pollApi(d.request_id, d.year, d.quarter);
            }
          });
      },
      error: () => {
        this.showLoader = false;
        this.toastType = ToastType.Error;
        this.showToast = true;
        this.toastMessage = 'There was an error fetching data summary';
        setTimeout(() => (this.showToast = false), 3000);
      },
    });
  }

  updatePagination() {
    const totalItems = this.refreshData?.length || 0;
    this.numberOfPages = Math.ceil(totalItems / this.pageSize) || 1;
    if (this.currentPage < 1) {
      this.currentPage = 1;
    } else if (this.currentPage > this.numberOfPages) {
      this.currentPage = this.numberOfPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.pagedData = this.refreshData.slice(start, end);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.numberOfPages) {
      return; // Ignore invalid page numbers
    }
    this.currentPage = page;
    this.updatePagination();
    this.selectedEngagementIndex = -1;
  }

  onPageSizeChange(option: Option) {
    this.selectedPageSize = option;
    this.pageSize = option.value;
    this.currentPage = 1; // Reset to first page when page size changes
    this.updatePagination();
    try {
      localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(option.value));
    } catch (e) {
      //ignore
    }
  }

  setCurrentEngagement(index: number): void {
    this.selectedEngagementIndex = index;
  }

  showStatusTooltip(index: number) {
    this.tooltipRowIndex = index;
  }

  hideStatusTooltip() {
    this.tooltipRowIndex = null;
  }

  goToViewScreen(year: any, quarter: any) {
    this.router.navigate(['/view-details'], {
      queryParams: { year: year, quarter: quarter },
    });
  }

  // remove panel open/close loader hooks as we now only load on Fetch Data button
  getTooltopScript(data: any) {
    let name = '';
    if (data) {
      name =
        'INGESTION' +
        ' ' +
        data.INGESTION +
        '%' +
        '\n' +
        '  CLASSIFICATION' +
        ' ' +
        data.CLASSIFICATION +
        // '%' +
        // '\n' +
        // '  VECTORIZATION' +
        // ' ' +
        // data.VECTORIZATION +
        '%';
    }
    return name;
  }

  getOverallPercent(p: any): number {
    if (!p) return 0;
    const vals = ['INGESTION', 'CLASSIFICATION']
      .map(k => Number(p[k] ?? 0));
    const sum = vals.reduce((a, b) => a + b, 0);
    return Math.round(sum / vals.length);
  }

  getStepClass(step?: number) {
    const v = Number(step ?? 0);
    if (v >= 100) return 'done';
    if (v > 0) return 'progress';
    return 'idle';
  }

  getOverallStateClass(status?: string) {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED': return 'overall-done';
      case 'FAILED': return 'overall-failed';
      case 'IN_PROGRESS': return 'overall-progress';
      default: return 'overall-idle';
    }
  }

  onRefresh() {
    if (this.refreshData.length === 0) {
      this.service.refreshApi().subscribe((data) => {
        this.runApi();
      });
    } else {
      this.service.refreshApi().subscribe((data) => {
        this.runApi();
      });
    }
  }
  openRefreshDialog(element: any) {
    // hide loader before showing confirmation dialog
    this.showLoader = false;
    const confirmation = this.dialog.open(RefreshDialogComponent, {
      width: '350px',
      disableClose: true,
      data: { data: element },
    });
    confirmation.afterClosed().subscribe((result) => {
      // ensure loader hidden after dialog
      this.showLoader = false;
      this.runApi();
    });
  }
  openStatusDialog() {
    let confirmation = this.dialog.open(IngestionStatusPopupComponent, {
      width: '350px',
      disableClose: true,
    });
    confirmation.afterClosed().subscribe((result) => {});
  }
  startOrchestration(year: any, quarter: any) {
    const key = `${year}_${quarter}`;
    this.processingKey = key;
    this.service.startOrchestration(year, quarter).subscribe({
      next: (data) => {
        if (data.request_id) {
          this.refreshData.forEach((d: any) => {
            if (d.year === year && d.quarter === quarter) {
              d.enable_ingestion = false;
              d.request_id = data.request_id;
            }
          });
          this.updatePagination();
          this.cdr.detectChanges();
          this.toastType = ToastType.Info;
          this.showToast = true;
          this.toastMessage = `Processing of documents has been started`;
          setTimeout(() => (this.showToast = false), 3000);

          // Only start polling if not already polling for this year/quarter
          const pollKey = `${year}_${quarter}`;
          if (!this.activePolls.has(pollKey)) {
            this.pollApi(data.request_id, year, quarter);
          }
        } else {
          this.openStatusDialog();
        }
        if (data.overall_status === 'COMPLETED') {
          this.runApi();
        }
      },
      error: () => {
        this.toastError('while starting document processing');
      },
      complete: () => {
        this.processingKey = null;
      },
    });
  }

  pollApi(requestId: string, year: number, quarter: string) {
    const pollKey = `${year}_${quarter}`;
    this.activePolls.add(pollKey);
    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.service.pollApi(requestId)),
        takeWhile((resp) => resp.overall_status === 'IN_PROGRESS', true),
        tap((data) => {
          this.refreshData = this.refreshData.map((d: any) => {
            if (d.year === year && d.quarter === quarter) {
              return {
                ...d,
                percent_completion: data.percent_completion,
                overall_status: data.overall_status,
                enable_ingestion: data.overall_status === 'COMPLETED',
                completed_at: data.completed_at ?? d.completed_at,
              };
            }
            return d;
          });
          this.updatePagination();
          this.cdr.detectChanges();
          // If polling is done (not IN_PROGRESS), remove from activePolls
          if (data.overall_status !== 'IN_PROGRESS') {
            this.activePolls.delete(pollKey);
          }
        })
      )
      .subscribe({
        complete: () => {
          this.activePolls.delete(pollKey);
        },
        error: () => {
          this.activePolls.delete(pollKey);
        },
      });
  }
  convertDateTommDDyyyy(date: any) {
    return `${
      date.split('-')[1] + '-' + date.split('-')[0] + '-' + date.split('-')[2]
    }`;
  }

  checkCookieConsent(): void {
    const consent = this.cookieConsent.getConsent();
    // If only essential exists (default) or no explicit consent record, show notice
    if (!consent || Object.keys(consent).length <= 1) {
      this.showCookieAlert = true;
    }
  }

  acceptCookies(): void {
    this.cookieConsent.acceptAll();
    this.showCookieAlert = false;
  }

  dismissCookieAlert(): void {
    this.showCookieAlert = false;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.tenantIdSubscription) {
      this.tenantIdSubscription.unsubscribe();
    }
  }
}
